import { Component, OnInit, Input } from '@angular/core';
import { TransactionType, Account } from './../../../../models/common/trading-common.model';
import { MutualFundDetail, BuyAtNavIndicator } from './../../../../models/mutualfund/trading-mutualfund.model';
import { TradingEntitlements } from './../../../../models/common/trading-entitlements';
import { TradingService } from '../../../../services/common/trading-service.service';
@Component({
    selector: 'cancel-mf-order-details',
    templateUrl: './cancel-mf-order-details.component.html',
    styleUrls: ['./cancel-mf-order-details.component.css']
})
export class CancelMfOrderDetailsComponent implements OnInit {
    @Input() order: any;
    @Input() nonDollarTrade: boolean;
    @Input() isBdUser: boolean;
    @Input() isRiaUser: boolean;
    @Input() tradingEntitlements: TradingEntitlements;
    @Input() account: Account;
    @Input() selectedSession: string;

    orderTitleStyle: string = 'tr-OrderSubTitle_Buy';
    orderAmount: number;
    isBuy: boolean;
    isRedeem: boolean;
    isExchange: boolean;
    showBalanceType: boolean = false;
    isCurrencyAmount: boolean = false;
    isNoLoadFund: boolean;
    orderAmountCurrency: string;
    showDvre: boolean = false;
    showRedemptionFeeWaiver: boolean = false;
    showComments: boolean = false;
    showShortSettle: boolean = false;
    fundCutoffTime: Date;
    fundVestDerivedVal: string;
    isFrontEndLoadFund: boolean;
    isBackEndOrDualLoadFund: boolean;
    eligibleToBuyAtNav: string;
    loiRoaAmtCurrency: string;
    hideUnusedLevel4Fields: boolean;

    showAdditionalInfo: boolean = false;
    showDeliver: boolean = false;
    showMutualFundComments: boolean = false;
    showSolicited: boolean;

    showIpSplitCommission: boolean;
    showRegulatoryInfo: boolean;
    activeTheme: string;
    constructor(private tradingService: TradingService) { }

    ngOnInit() {
        this.activeTheme = this.tradingService.getActiveThemeFromCurrentContainer();
        // core section data starts
        const transactionType = this.order.transactionType;
        this.setDisplayDetailsForTransactionType(transactionType);

        // The tradeInShares value is never returned by order details, and we must derive it
        this.order.tradeInShares = !!this.order.shareQty && this.order.shareQty > 0;
        this.orderAmount = this.order.tradeInShares ? this.order.shareQty : this.order.dollarQty;
        this.isCurrencyAmount = !this.order.tradeInShares;
        this.setOrderAmountCurrency();

        if ((this.isBdUser && this.isBuy && (typeof this.order.solicited === 'boolean')) || (this.tradingEntitlements.isRetailUser && (this.tradingEntitlements.isSolicitedEnabled || this.tradingEntitlements.isNonSolicitedEnabled))) {
            this.showSolicited = true;
        }

        this.showDvre = this.isDvreDisplayRequired(transactionType);
        this.showRedemptionFeeWaiver = this.isRedemptionFeeWaiverEnabled(transactionType);
        this.showComments = this.isCommentsEnabled();
        this.showShortSettle = this.isShortSettleEnabled(transactionType);

        if (!!this.order.mutualFund) {
            this.setMutualFundDetailDisplayData();
        }
        // core section data ends

        // addtional info data starts
        if (this.isBdUser) {
            this.setAdditionalInfoDisplayFlagsForBdUser();
        } else {
            this.showAdditionalInfo = this.isAdditionalInfoDisplayEnabled();
        }
        // addtional info data ends

        // IP Split Info data starts
        if (!!this.order.commission) {
            this.showIpSplitCommission = !this.tradingEntitlements.isRetailUser && (!!this.order.commission.splitIP1 ||
                !!this.order.commission.groupId);
        }
        // IP Split Info data ends

        // Regulatory Info data starts
        this.showRegulatoryInfo = this.isRegulatoryInfoDisplayEnabled();
        // Regulatory Info data ends
    }

    private setDisplayDetailsForTransactionType(transactionType) {
        this.isExchange = (transactionType === TransactionType.EXCHANGE || transactionType === TransactionType.EXCHANGE_ALL);
        this.isRedeem = (transactionType === TransactionType.REDEEM || transactionType === TransactionType.REDEEM_ALL);

        if (transactionType === TransactionType.BUY) {
            this.isBuy = true;
            this.orderTitleStyle = 'tr-OrderSubTitle_Buy';
        } else {
            this.isBuy = false;
            this.orderTitleStyle = this.isExchange ? '' : 'tr-OrderSubTitle_Sell';
            this.showBalanceType = this.order.account.businessPurposeCd === 'BNKC';
        }
    }

    private setOrderAmountCurrency() {
        if (this.isCurrencyAmount) {
            const fundDtl: MutualFundDetail = this.order.mutualFund;
            this.orderAmountCurrency = !!fundDtl && !!fundDtl.currencyCode && fundDtl.currencyCode !== '' && fundDtl.currencyCode !== 'USD' ? fundDtl.currencyCode : 'USD';
        }
    }

    private isCommentsEnabled(): boolean {
        return this.isRiaUser && !!this.order.trailer;
    }

    private isShortSettleEnabled(transactionType): boolean {
        return (this.isRiaUser && this.order.shortSettle && (transactionType === TransactionType.REDEEM
            || transactionType === TransactionType.REDEEM_ALL) && this.tradingEntitlements.isMutualFundShortSettleEnabled
            && this.order.mutualFund && this.order.mutualFund.shortSettlementReasonCode === 'Y');
    }

    private isRedemptionFeeWaiverEnabled(transactionType): boolean {
        return (this.isRiaUser && (transactionType === TransactionType.REDEEM || transactionType === TransactionType.REDEEM_ALL));
    }

    private isDvreDisplayRequired(transactionType): boolean {
        return ((transactionType !== TransactionType.REDEEM && transactionType !== TransactionType.REDEEM_ALL) && !!this.order.dvre);
    }

    private isRegulatoryInfoDisplayEnabled(): boolean {
        return !this.tradingEntitlements.isRetailUser && !this.isRiaUser && (!!this.order.secIbdOrdRcvDate ||
            !!this.order.secTermsAndConditions ||
            !!this.order.secIbdOrdRcvFromName ||
            !!this.order.oatsDeskReceiptDate1 ||
            !!this.order.oatsDeskTypeCode1 ||
            !!this.order.ordXctnTxt1 ||
            !!this.order.oatsDeskReceiptDate2 ||
            !!this.order.oatsDeskTypeCode2 ||
            !!this.order.ordXctnTxt2);
    }

    private setMutualFundDetailDisplayData() {
        this.fundCutoffTime = new Date('1970-01-01T' + this.order.mutualFund.purchaseCutoffTime + '-05:00');

        let fundVestCode: string = this.order.mutualFund.ntfParticipationCode;

        if (fundVestCode === 'R' || fundVestCode === 'I' || fundVestCode === 'O') {
            this.fundVestDerivedVal = 'yes';
        } else if (fundVestCode === 'N' || fundVestCode === 'U' || fundVestCode === ' ') {
            this.fundVestDerivedVal = 'no';
        } else {
            this.fundVestDerivedVal = 'notApplicable';
        }

        this.isNoLoadFund = this.order.mutualFund.loadNoLoadInd === 'N' || this.order.mutualFund.loadNoLoadInd === 'X';
        this.isFrontEndLoadFund = this.order.mutualFund.loadNoLoadInd === 'L';
        this.isBackEndOrDualLoadFund = this.order.mutualFund.loadNoLoadInd === 'B' || this.order.mutualFund.loadNoLoadInd === 'D';

        if (this.isFrontEndLoadFund && this.isBdUser && this.isBuy) {
            // On a Front-End Load Fund Buy order for a BD user, buyAtNav will
            // only be NO when the user selected "NO" for Eligible to Buy At NAV
            // in the form UI
            this.eligibleToBuyAtNav = this.order.buyAtNav === BuyAtNavIndicator.NO ? 'NO' : 'YES';
            let fundDtl: MutualFundDetail = this.order.mutualFund;
            this.loiRoaAmtCurrency =
                !!fundDtl && !!fundDtl.currencyCode && fundDtl.currencyCode !== '' && fundDtl.currencyCode !== 'USD' ? fundDtl.currencyCode : '$';
        }

        this.hideUnusedLevel4Fields = this.account.beingTradedInLevel4PlanAcct;
    }

    private isAdditionalInfoDisplayEnabled(): boolean {
        return  !this.tradingEntitlements.isRetailUser && ((!!this.order.addlOrdData && this.order.addlOrdData.length > 0) ||
                !!this.order.floorInst ||
                !!this.order.trailer ||
                (!!this.order.discExercised && !!this.order.discExercised.trim()));
    }
    
    private setAdditionalInfoDisplayFlagsForBdUser() {
        this.showAdditionalInfo = ((!!this.order.addlOrdData && this.order.addlOrdData.length > 0) ||
                !!this.order.floorInst ||
                !!(this.order.discExercised || '').trim() ||
                (!!this.order.paInd && !this.nonDollarTrade) ||
                (!!(this.order.addlIbdMemo || '').trim() && !this.nonDollarTrade) ||
                !!this.order.trailer ||
                (!!this.order.sourceOverride && !this.nonDollarTrade));

            let isPlan529Account: boolean = !!this.account && !!this.account.acctCatgCd && (this.account.acctCatgCd.indexOf('529') > -1);

            let fundDetail = this.order.mutualFund || {};
            let isFrontEndLoadFund: boolean = fundDetail.loadNoLoadInd === 'L';
            let isBackEndOrDualLoadFund: boolean = fundDetail.loadNoLoadInd === 'B' || fundDetail.loadNoLoadInd === 'D';

            this.showDeliver = !isPlan529Account && (isFrontEndLoadFund || isBackEndOrDualLoadFund) && !!this.order.deliver;
            this.showMutualFundComments = !!this.order.trailer;
    }
    
    formatBackendTime(sourceTime: string): string {
        if (!!sourceTime && sourceTime !== '') {
            return sourceTime.substring(0, 2) + ':' + sourceTime.substring(2, 4) + ':' + sourceTime.substring(4);
        } else {
            return sourceTime;
        }
    }

    formatBackendDate(sourceDate: string): string {     // yyyymmdd => mm/dd/yyyy
        if (!!sourceDate) {
            return sourceDate.substring(4, 6) + '/' + sourceDate.substring(6, 8) + '/' + sourceDate.substring(0, 4);
        } else {
            return '';
        }
      }
}
